import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getFiltersFromQuery,
  getQueryFromFilters,
  sanitizeQueryRegex,
} from 'lib/helpers';
import { orderBy } from 'lodash';
import { useRouter } from 'next/router';

import useFetch from 'lib/hooks/useFetch';

import { selectCurrentUser } from 'features/auth/slice';

import { Heading, PageHeader } from '@frontendmentorio/design-system-v2';

import ChallengeCard from 'features/challenges/Card';
import Discord from 'features/shared/Discord';
import FilterAndSort from './FilterAndSort';
import Info from './Info';
import UnlockPro from './UnlockPro';

export const DEFAULT_FILTERS = {
  difficulty: [],
  languages: [],
  search: '',
  sort: 'createdAt|desc',
  status: [],
  type: [],
};

export const filterSort = ({
  challenges,
  completedIds = [],
  filters: { sort, ...filters },
  inProgressIds = [],
}) => {
  const results = challenges.filter((challenge) => {
    return Object.keys(filters).every((key) => {
      if (filters[key] instanceof Array && !filters[key].length) return true;

      switch (key) {
        case 'languages':
          return filters[key].includes(challenge[key].slice(-1)[0]);
        case 'search':
          return challenge.title.match(sanitizeQueryRegex(filters[key]));
        case 'status':
          return filters[key].some((status) => {
            switch (status) {
              case 'completed':
                return completedIds.includes(challenge.id);
              case 'in-progress':
                return (
                  inProgressIds.includes(challenge.id) &&
                  !completedIds.includes(challenge.id)
                );
              case 'not-started':
                return (
                  !inProgressIds.includes(challenge.id) &&
                  !completedIds.includes(challenge.id)
                );
            }
          });
        default:
          return filters[key].includes(challenge[key]);
      }
    });
  });

  const [key, order] = sort.split('|');

  return orderBy(results, [key], [order]);
};

export default function Index({ challenges }) {
  const router = useRouter();
  const currentUser = useSelector(selectCurrentUser);

  const query = router.asPath.replace(router.pathname, '');
  const initialFilters = getFiltersFromQuery(query, DEFAULT_FILTERS);
  const [filters, setFilters] = useState(initialFilters);

  const {
    data: { data: completed },
  } = useFetch('/api/v3/learner/challenges/completed');

  const completedIds = completed?.map((challenge) => challenge.id);

  const filtered = filterSort({
    challenges,
    completedIds,
    filters,
    inProgressIds: currentUser?.unlockedHubs,
  });

  useEffect(() => {
    const query = getQueryFromFilters(filters, DEFAULT_FILTERS);
    const route = query.length ? `/challenges?${query}` : '/challenges';

    router.replace(route, undefined, { scroll: false });
  }, [filters]);

  return (
    <>
      <PageHeader heading="Challenges" />

      <h1 className="sr-only">Challenges</h1>

      <div className="flex flex-col gap-6 px-5 py-6">
        <div className="container-xl flex flex-col gap-5">
          {!currentUser?.hasActiveSubscription && <UnlockPro />}

          <FilterAndSort filters={filters} setFilters={setFilters} />

          <Info challenges={challenges} filtered={filtered} />

          {filtered.length > 0 && (
            <ul className="gap-5 grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3">
              {filtered.map((challenge) => (
                <ChallengeCard
                  challenge={challenge}
                  isCompleted={completedIds?.includes(challenge.id)}
                  key={challenge.id}
                />
              ))}
            </ul>
          )}

          {challenges.length && !filtered.length && (
            <div className="flex flex-col items-center px-5 py-8 text-center w-full">
              <Heading level="6" tagName="h2">
                No challenges match those filters 🤔
              </Heading>
            </div>
          )}
        </div>

        <Discord />
      </div>
    </>
  );
}
